import type { VectorNetwork } from "@/document/model/vectorNetwork";
import type { EditorSelectionState } from "@/commands/Command";
import type { CurvaDocument } from "@/document/model/document";
import type { AnimationExportFormat } from "@/export/animationExporter";
import type { AnimationGraphVisualGroup, AnimationNodeKind } from "@/document/model/animation";
import type { AnimationLoopRegion, AnimationTimelineViewport } from "@/editor/shell/AnimationTimeline";
import type { GuideSelection } from "@/document/model/guides";
import type { RichTextModel } from "@/document/model/text";
import type { VectorHandleUpdate } from "@/document/operations/vectorHandleMirroring";
import type { ColorSamplerRequest } from "@/editor/shell/colorSampler";
import type { CanvasPointerStatus } from "@/editor/shell/canvasWorkspaceTypes";
import type { ActiveTool } from "@/editor/shell/TopBar";
import type { PaintTarget } from "@/editor/shell/right-panel/types";
import type { PrimitiveKind } from "@/document/model/primitives";
import type { Camera } from "@/renderer/camera";
import type { RenderPreview } from "@/renderer/overlayTypes";
import type { Rect } from "@/lib/math/rect";

export type EditorMode = EditorSelectionState["mode"];
export type RightPanelTab = "design" | "prototype" | "dev" | "scripts";
export type UiChromeMode = "visible" | "hidden" | "tools" | "toolsSidebar";

export interface ViewportTarget {
  x: number;
  y: number;
  token: number;
}

export interface ComponentPlacementState {
  componentId: string;
  token: number;
}

export interface VectorVertexPreviewState {
  nodeId: string;
  vertexId: string;
  x: number;
  y: number;
  vertices?: Array<{ vertexId: string; x: number; y: number }>;
  handles?: Array<{ edgeId: string; endpoint: "from" | "to"; x: number; y: number }>;
}

export interface VectorHandlePreviewState {
  nodeId: string;
  edgeId: string;
  endpoint: "from" | "to";
  x: number;
  y: number;
  mirroredHandle?: VectorHandleUpdate;
}

export interface VectorEdgeBendPreviewState {
  nodeId: string;
  edgeId: string;
  fromHandle: { x: number; y: number };
  toHandle: { x: number; y: number };
}

export interface SculptBrushState {
  size: number;
  strength: number;
}

export interface SculptBrushPreviewState {
  nodeId: string;
  x: number;
  y: number;
  radius: number;
}

export interface VectorTopologyPreviewState {
  nodeId: string;
  network: VectorNetwork;
  changedEdgeIds: string[];
  changedVertexIds: string[];
  newVertexIds: string[];
  /** Extruded edge copies (Extrude tool); omitted for other topology previews. */
  newEdgeIds?: string[];
}

export interface SubdivideSessionState {
  nodeId: string;
  edgeIds: string[];
  segments: number;
  connect: boolean;
  previousTool: ActiveTool;
  token: number;
}

export interface TextSelectionState {
  nodeId: string;
  anchor: number;
  focus: number;
  focused: boolean;
}

export interface FillEditTarget {
  nodeId: string;
  target: PaintTarget;
}

export interface SelectionPaintPickerRequest {
  kind: "fill" | "stroke";
  nodeIds: string[];
  selectionNodeIds: string[];
  primaryNodeId?: string;
  token: number;
}

export interface ContextMenuState {
  nodeId: string;
  x: number;
  y: number;
}

export interface ComponentImportProgressState {
  phase: "reading" | "importing" | "previews";
  filename: string;
  current: number;
  total: number;
}

export interface EditorToolState {
  activeTool: ActiveTool;
  selectedShapeKind: PrimitiveKind;
}

export interface AnimationUiState {
  panelOpen: boolean;
  timelineOpen: boolean;
  previewWindowOpen: boolean;
  playing: boolean;
  timeMs: number;
  activeRootId?: string;
  armedNodeKind?: AnimationNodeKind;
  loopRegion?: AnimationLoopRegion;
  timelineViewports: Record<string, AnimationTimelineViewport>;
  exportingFormat?: AnimationExportFormat;
  vectorTargetNodeId?: string;
}

export interface CanvasDebugState {
  showPixelGrid: boolean;
  showRulers: boolean;
  showRenderDebug: boolean;
  renderTriangles: boolean;
  showTileGaps: boolean;
  /** Prefer curva-tess Wasm mesh/curve kernels over the TS fallback (dev A/B). */
  useWasmTessellation: boolean;
}

export interface CanvasSnapState {
  snapToPixelGrid: boolean;
  snapToObjects: boolean;
  snapToGuides: boolean;
  snapToGeometry: boolean;
  assistedPencilTracing: boolean;
}

export interface ScriptDebugLogEntry {
  id: string;
  level: "log" | "error" | "info";
  message: string;
  source?: string;
  ts: number;
}

/** Realtime script preview modal state (working-copy pages from the session). */
export interface ScriptPreviewUiState {
  frameId: string;
  frameName?: string;
  pages: CurvaDocument["pages"];
  revision: number;
}

/** Ghost-frame overlay while editing script @param values (before Run). */
export type ScriptParamPreviewUiState = ScriptPreviewUiState;

export function cameraStateEqual(left: Camera, right: Camera): boolean {
  return left.x === right.x
    && left.y === right.y
    && left.zoom === right.zoom
    && left.devicePixelRatio === right.devicePixelRatio;
}

export function rectStateEqual(left: Rect | undefined, right: Rect | undefined): boolean {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return left.x === right.x
    && left.y === right.y
    && left.width === right.width
    && left.height === right.height;
}

export function stringArrayStateEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}
